/* Theme-owned additions to UpCart. Its checkout, rewards and item controls stay app-owned. */
(() => {
  const clone = (id) => document.getElementById(id)?.content.cloneNode(true);
  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };

  const FR_WEEKDAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

  function updateShippingEstimate(root) {
    const estimate = root.querySelector('[data-shipping-estimate]');
    const receive = estimate?.querySelector('[data-shipping-receive]');
    if (!estimate || !receive) return;
    const days = parseInt(estimate.dataset.days, 10) || 1;
    const cutoffHour = parseInt(estimate.dataset.cutoffHour, 10);
    const skipSunday = estimate.dataset.skipSunday === 'true';
    const now = new Date();
    let addDays = days;
    if (!Number.isNaN(cutoffHour) && now.getHours() >= cutoffHour) addDays += 1;
    const delivery = new Date(now.getFullYear(), now.getMonth(), now.getDate() + addDays);
    if (skipSunday) {
      while (delivery.getDay() === 0) delivery.setDate(delivery.getDate() + 1);
    }
    setText(receive, FR_WEEKDAYS[delivery.getDay()]);
  }

  function highlightRewardAmount(message) {
    if (!message) return;
    const walker = document.createTreeWalker(message, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) {
      if (!walker.currentNode.parentElement.closest('.forme-reward-amount')) nodes.push(walker.currentNode);
    }
    // UpCart's rich text templates only bold the amount in some reward tiers.
    for (const node of nodes) {
      const matches = [...node.textContent.matchAll(/\d[\d.,\s]*€/g)];
      if (!matches.length) continue;
      const fragment = document.createDocumentFragment();
      let offset = 0;
      for (const match of matches) {
        fragment.append(node.textContent.slice(offset, match.index));
        const amount = document.createElement('span');
        amount.className = 'forme-reward-amount';
        amount.textContent = match[0];
        fragment.append(amount);
        offset = match.index + match[0].length;
      }
      fragment.append(node.textContent.slice(offset));
      node.replaceWith(fragment);
    }
  }

  class CartRecommendations extends HTMLElement {
    revealedOptions = new WeakSet();

    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.addEventListener('click', this.onClick, options);
      this.addEventListener('keydown', this.onKeydown, options);
      this.addEventListener('touchstart', this.onTouchStart, { ...options, passive: true });
      this.addEventListener('touchmove', this.onTouchMove, { ...options, passive: false });
      this.addEventListener('touchend', this.onTouchEnd, options);
      this.addEventListener('touchcancel', this.cancelDrag, options);
      this.addEventListener('pointerdown', this.onPointerDown, options);
      this.addEventListener('pointermove', this.onPointerMove, options);
      this.addEventListener('pointerup', this.onPointerUp, options);
      this.addEventListener('pointercancel', this.cancelDrag, options);
      this.addEventListener('scroll', this.updateNavigation, { ...options, capture: true });
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(this.updateNavigation);
      this.querySelectorAll('[role="tabpanel"]').forEach((panel) => this.resizeObserver.observe(panel));
      this.updateNavigation();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.cancelDrag();
    }

    userSetExpanded(expanded) {
      this.dispatchEvent(new Event('forme:recommendations-toggle', { bubbles: true }));
      this.setExpanded(expanded);
    }

    startDrag(x, y, target) {
      if (target.closest('.forme-recommendation-options')) return;
      const expanded = this.classList.contains('is-expanded');
      const handle = target.closest('.forme-recommendations-toggle');
      if (!expanded && !handle) return;
      if (!handle && target.closest('select, input, button')) return;
      const panel = target.closest('[role="tabpanel"]');
      if (panel && panel.scrollTop > 0) return;
      this.drag = { x, y, expanded, direction: expanded ? 1 : -1, distance: 0, active: false };
    }

    moveDrag(x, y, event) {
      if (!this.drag) return;
      const dx = x - this.drag.x;
      const dy = y - this.drag.y;
      const distance = dy * this.drag.direction;
      if (!this.drag.active) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (distance <= 0 || Math.abs(dx) > Math.abs(dy)) return this.cancelDrag();
        this.drag.active = true;
        this.dispatchEvent(new Event('forme:recommendations-toggle', { bubbles: true }));
        this.classList.add('is-dragging');
      }
      if (event.cancelable) event.preventDefault();
      this.drag.distance = Math.max(0, distance);
      this.style.setProperty('--forme-drag-offset', `${this.drag.direction * Math.min(this.drag.distance, 180)}px`);
    }

    finishDrag() {
      if (this.drag?.active) {
        this.suppressClickUntil = Date.now() + 400;
        if (this.drag.distance >= 48) this.userSetExpanded(!this.drag.expanded);
      }
      this.cancelDrag();
    }

    cancelDrag = () => {
      this.drag = null;
      this.optionDrag = null;
      if (this.classList.contains('is-dragging')) this.classList.remove('is-dragging');
      this.style.removeProperty('--forme-drag-offset');
    };

    onTouchStart = (event) => {
      if (event.touches.length !== 1) return this.cancelDrag();
      const touch = event.touches[0];
      this.startDrag(touch.clientX, touch.clientY, event.target);
    };
    onTouchMove = (event) => {
      if (event.touches.length !== 1) return this.cancelDrag();
      const touch = event.touches[0];
      this.moveDrag(touch.clientX, touch.clientY, event);
    };
    onTouchEnd = () => this.finishDrag();

    onPointerDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      const group = event.target.closest('.forme-recommendation-options');
      if (group) {
        this.optionDrag = { group, x: event.clientX, scrollLeft: group.scrollLeft, active: false };
        return;
      }
      if (!event.target.closest('.forme-recommendations-toggle')) return;
      this.startDrag(event.clientX, event.clientY, event.target);
    };
    onPointerMove = (event) => {
      if (event.pointerType !== 'mouse') return;
      if (this.optionDrag) {
        const distance = event.clientX - this.optionDrag.x;
        if (!this.optionDrag.active && Math.abs(distance) < 6) return;
        this.optionDrag.active = true;
        event.preventDefault();
        if (!this.hasPointerCapture(event.pointerId)) this.setPointerCapture(event.pointerId);
        this.optionDrag.group.scrollLeft = this.optionDrag.scrollLeft - distance;
        return;
      }
      this.moveDrag(event.clientX, event.clientY, event);
      if (this.drag?.active && !this.hasPointerCapture(event.pointerId)) this.setPointerCapture(event.pointerId);
    };
    onPointerUp = (event) => {
      if (event.pointerType !== 'mouse') return;
      if (this.optionDrag?.active) this.suppressClickUntil = Date.now() + 400;
      this.finishDrag();
      if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    };

    updateNavigation = () => {
      const panel = this.querySelector('[role="tabpanel"]:not([hidden])');
      if (!panel) return;
      panel.querySelectorAll('.forme-recommendation-options').forEach((group) => {
        if (!group.clientWidth || this.revealedOptions.has(group)) return;
        this.revealedOptions.add(group);
        group.scrollLeft = Math.max(0, (group.querySelector('[aria-checked="true"]')?.offsetLeft || 0) - parseFloat(getComputedStyle(group).paddingLeft));
      });
      const maximum = panel.scrollWidth - panel.clientWidth;
      this.querySelectorAll('[data-scroll-direction]').forEach((button) => {
        button.setAttribute('aria-controls', panel.id);
        button.disabled = Number(button.dataset.scrollDirection) < 0 ? panel.scrollLeft <= 1 : panel.scrollLeft >= maximum - 1;
      });
    };

    setExpanded(expanded) {
      if (this.classList.contains('is-expanded') === expanded) return;
      if (!expanded) this.hideOptions();
      this.classList.toggle('is-expanded', expanded);
      this.querySelector('.forme-recommendations-toggle').setAttribute('aria-expanded', String(expanded));
      this.querySelector('.forme-recommendations-content').inert = !expanded;
    }

    selectTab(tab) {
      this.hideOptions();
      this.querySelectorAll('[role="tab"]').forEach((item) => {
        item.setAttribute('aria-selected', String(item === tab));
        item.tabIndex = item === tab ? 0 : -1;
      });
      this.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
        panel.hidden = panel.id !== tab.getAttribute('aria-controls');
      });
      this.updateNavigation();
    }

    onKeydown = (event) => {
      if (event.target.matches('.forme-recommendation-option')) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          const button = this.activeOptionCard.querySelector('.forme-recommendation-add');
          this.hideOptions();
          button.focus({ preventScroll: true });
          return;
        }
        const options = [...event.target.closest('[role="radiogroup"]').querySelectorAll('button:not(:disabled)')];
        let index = options.indexOf(event.target);
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') index = (index + 1) % options.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') index = (index - 1 + options.length) % options.length;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = options.length - 1;
        else return;
        event.preventDefault();
        this.selectVariant(options[index]);
        options[index].focus({ preventScroll: true });
        return;
      }
      if (!event.target.matches('[role="tab"]')) return;
      const tabs = [...this.querySelectorAll('[role="tab"]')];
      let index = tabs.indexOf(event.target);
      if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else return;
      event.preventDefault();
      this.selectTab(tabs[index]);
      tabs[index].focus();
    };

    selectVariant(option) {
      if (option.disabled || this.busy) return;
      const card = this.activeOptionCard;
      if (!card) return;
      const variant = card.querySelector('[data-recommendation-variant]');
      variant.value = option.dataset.variantId;
      variant.dataset.min = option.dataset.min;
      option.parentElement.querySelectorAll('.forme-recommendation-option').forEach((button) => {
        button.setAttribute('aria-checked', String(button === option));
        button.tabIndex = button === option ? 0 : -1;
      });
      const group = option.parentElement;
      const left = option.offsetLeft;
      if (left < group.scrollLeft || left + option.offsetWidth > group.scrollLeft + group.clientWidth) {
        group.scrollLeft = Math.max(0, left - parseFloat(getComputedStyle(group).paddingLeft));
      }
      setText(card.querySelector('[data-recommendation-price]'), option.dataset.price);
      setText(card.querySelector('[data-recommendation-compare]'), option.dataset.compare);
      let badge = card.querySelector('.forme-recommendation-discount');
      if (!badge && option.dataset.discount) {
        badge = document.createElement('span');
        badge.className = 'forme-recommendation-discount';
        card.querySelector('.forme-recommendation-image').append(badge);
      }
      if (badge) {
        badge.hidden = !option.dataset.discount;
        setText(badge, `-${option.dataset.discount}%`);
      }
    }

    hideOptions() {
      if (this.activeOptionCard && this.activeOptionGroup) {
        this.activeOptionGroup.hidden = true;
        this.activeOptionCard.append(this.activeOptionGroup);
        this.activeOptionCard.querySelector('.forme-recommendation-add').setAttribute('aria-expanded', 'false');
      }
      this.querySelector('.forme-options-banner').hidden = true;
      this.activeOptionCard = null;
      this.activeOptionGroup = null;
    }

    getCardOptions(card) {
      return this.activeOptionCard === card ? this.activeOptionGroup : card.querySelector('.forme-recommendation-options');
    }

    showOptions(card) {
      this.hideOptions();
      const group = card.querySelector('.forme-recommendation-options');
      const banner = this.querySelector('.forme-options-banner');
      this.activeOptionCard = card;
      this.activeOptionGroup = group;
      setText(banner.querySelector('.forme-options-banner-title'), card.querySelector('.forme-recommendation-title').textContent);
      banner.querySelector('.forme-options-banner-content').append(group);
      banner.hidden = false;
      group.hidden = false;
      card.querySelector('.forme-recommendation-add').setAttribute('aria-expanded', 'true');
      const option = group.querySelector('[aria-checked="true"]:not(:disabled)') || group.querySelector('button:not(:disabled)');
      option?.focus({ preventScroll: true });
      if (option) group.scrollLeft = Math.max(0, option.offsetLeft - parseFloat(getComputedStyle(group).paddingLeft));
    }

    onClick = async (event) => {
      if (Date.now() < (this.suppressClickUntil || 0)) {
        event.preventDefault();
        return;
      }
      if (event.target.closest('.forme-recommendations-toggle')) {
        this.userSetExpanded(!this.classList.contains('is-expanded'));
        return;
      }
      if (event.target.closest('.forme-options-banner-close')) {
        const button = this.activeOptionCard?.querySelector('.forme-recommendation-add');
        this.hideOptions();
        button?.focus({ preventScroll: true });
        return;
      }
      const tab = event.target.closest('[role="tab"]');
      if (tab) return this.selectTab(tab);
      const option = event.target.closest('.forme-recommendation-option');
      if (option) {
        if (option.disabled || this.busy || option.closest('.forme-recommendation-options').hidden) return;
        this.selectVariant(option);
        return this.addVariant(this.activeOptionCard);
      }
      const arrow = event.target.closest('[data-scroll-direction]');
      if (arrow) {
        const panel = this.querySelector('[role="tabpanel"]:not([hidden])');
        const card = panel?.querySelector('.forme-recommendation-card');
        if (card) panel.scrollBy({
          left: Number(arrow.dataset.scrollDirection) * (card.getBoundingClientRect().width + parseFloat(getComputedStyle(panel).columnGap)),
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        });
        return;
      }
      const button = event.target.closest('.forme-recommendation-add');
      if (!button || button.disabled || this.busy) return;
      const card = button.closest('.forme-recommendation-card');
      if (this.getCardOptions(card)) return this.showOptions(card);
      return this.addVariant(card);
    };

    async addVariant(card) {
      if (this.busy) return;
      const button = card.querySelector('.forme-recommendation-add');
      const group = this.getCardOptions(card);
      const variant = card.querySelector('[data-recommendation-variant]');
      const status = this.querySelector('.forme-recommendations-status');
      this.busy = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      setText(status, '');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variant.value), quantity: Number(variant.dataset.min || 1) }] }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Impossible d’ajouter ce produit. Réessayez.');
        await window.upcartRefreshCart?.();
        schedule();
        if (group) {
          const restoreFocus = group.contains(document.activeElement);
          this.hideOptions();
          if (restoreFocus) {
            button.disabled = false;
            button.focus({ preventScroll: true });
          }
        }
      } catch (error) {
        setText(status, error.message || 'Impossible d’ajouter ce produit. Réessayez.');
      } finally {
        this.busy = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('forme-cart-recommendations')) customElements.define('forme-cart-recommendations', CartRecommendations);

  let cartRoot;
  let wasOpen = false;
  let expandTimer;
  let openAt = 0;
  let userToggled = false;
  let frame;
  let recommendations;
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(() => { frame = null; enhance(); });
  };

  function enhance() {
    const root = document.querySelector('#CartPopup');
    if (!root) return;
    if (cartRoot !== root) {
      cartRoot = root;
      new MutationObserver(schedule).observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });
      root.addEventListener('forme:recommendations-toggle', () => {
        userToggled = true;
        clearTimeout(expandTimer);
      });
    }
    if (!root.classList.contains('forme-sidecart')) root.classList.add('forme-sidecart');
    highlightRewardAmount(root.querySelector('.upcart-public-rewards__message'));
    if (/^fr(?:-|$)/i.test(document.documentElement.lang)) {
      root.querySelectorAll('.upcart-public-bundle-properties__header').forEach((label) => {
        const match = label.textContent.trim().match(/^(Show|Hide) (\d+) items?$/i);
        if (!match) return;
        const action = match[1].toLowerCase() === 'show' ? 'Afficher' : 'Masquer';
        setText(label, `${action} ${match[2]} article${Number(match[2]) === 1 ? '' : 's'}`);
      });
    }
    const header = root.querySelector('.upcart-public-header');
    if (header && !header.querySelector('.forme-sidecart-logo')) header.append(clone('forme-sidecart-header'));
    const cart = window.upcartGetCart?.();
    if (cart?.items) {
      setText(root.querySelector('[data-sidecart-count]'), String(cart.items.reduce((sum, item) => sum + item.quantity, 0)));
    }
    const footer = root.querySelector('.upcart-footer');
    if (footer) {
      if (!footer.querySelector('.forme-sidecart-reviews')) {
        footer.append(clone('forme-sidecart-footer'));
      }
      updateShippingEstimate(footer);
      if (!recommendations) {
        recommendations = clone('forme-sidecart-recommendations')?.querySelector('forme-cart-recommendations');
      }
      // Keep the glass panel above the cart body and below the opaque checkout footer.
      // A negative layer inside the footer blocks real pointer hits and backdrop sampling.
      if (recommendations && recommendations.parentElement !== footer.parentElement) footer.before(recommendations);
      const checkout = footer.querySelector('.upcart-checkout-button');
      if (checkout && !checkout.querySelector('.forme-checkout-content')) checkout.append(clone('forme-sidecart-checkout'));
      const total = footer.querySelector('.styles_Footer__cartSubtotalValue__')?.textContent.trim();
      if (total) setText(checkout?.querySelector('[data-sidecart-total]'), total);
      if (checkout) checkout.setAttribute('aria-label', checkout.querySelector('.forme-checkout-content')?.textContent.trim() || 'Valider le panier');
    }
    const hasRecommendations = Boolean(footer && recommendations);
    if (root.classList.contains('forme-has-recommendations') !== hasRecommendations) root.classList.toggle('forme-has-recommendations', hasRecommendations);

    const isOpen = root.classList.contains('styles_active__');
    if (isOpen && !wasOpen) {
      openAt = Date.now();
      userToggled = false;
      recommendations?.setExpanded(false);
      clearTimeout(expandTimer);
      expandTimer = setTimeout(() => {
        if (root.classList.contains('styles_active__') && !userToggled) recommendations?.setExpanded(true);
      }, 1000);
    } else if (!isOpen && wasOpen) {
      clearTimeout(expandTimer);
      recommendations?.setExpanded(false);
    }
    // If UpCart finishes rendering the products after the opening event, honour the same deadline.
    if (isOpen && recommendations && !userToggled && Date.now() - openAt >= 1000) recommendations.setExpanded(true);
    wasOpen = isOpen;
  }

  // UpCart loads asynchronously and may replace its root during theme-editor updates.
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  schedule();
})();
